import firebase from "firebase/app";
import { useEffect, useCallback } from "react";
import { useFirestore, useUser } from "reactfire";
import { auth } from "~/lib/firebase";
import cookie from "js-cookie";
import { useRouter } from "next/router";
import useProfile from "~/hooks/useProfile";
import { postTokenToRN } from "../ReactNativeBridge";
import { marketingNavItemsRight, newsFeedRoute } from "~/routes";
import { NetworkMembership } from "@converge-collective/common/models/Network";
import { clearFirestoreCache } from "~/lib/browser-util";

const userCookieName = "auth";
const userCookieExpires = 1;

const updateCookie = async (firebaseUser: firebase.User) => {
  console.log("update auth cookie", firebaseUser);
  // any time the token is updated store it in a cookie
  const token = await firebaseUser.getIdToken();
  const { uid, email } = firebaseUser;
  const user = {
    uid,
    token,
    email: email,
  };
  cookie.set(userCookieName, JSON.stringify(user), {
    expires: userCookieExpires,
  });

  // after setting the cookie, post the token up to RN (if this web app is
  // running inside RN)
  postTokenToRN(firebaseUser);
};

const removeCookie = () => {
  cookie.remove(userCookieName);
};

const Auth = (): React.ReactElement => {
  // do all crazy auth logic stuff here
  const firestore = useFirestore();
  const router = useRouter();
  const networkFromUrl = router.query?.network;

  // const billingsMarketingUrls = marketingNavItemsRight("billings").map(
  //   ({ url }) => url
  // );

  const { data: user } = useUser();
  console.log("Auth", { currentUser: auth.currentUser, user });
  const [profile] = useProfile(user?.uid);
  const profileRef = user && firestore.collection("profiles").doc(user.uid);

  /**
   * Returns undefined if:
   * - the profile is not yet loaded
   * - or the profile is loaded but the user is not a member of the network
   */
  const setCurrentNetworkMembership = useCallback(async (): Promise<
    NetworkMembership | undefined
  > => {
    // we can't do anything if the profile isn't available
    if (!profileRef || !profile) {
      return undefined;
    }

    // if the user already has one, return it
    if (profile.currentNetworkMembership) {
      return profile.currentNetworkMembership;
    }

    // if the user doesn't have a current network set yet, set one
    if (!profile.currentNetworkMembership) {
      let currentNetworkMembership: any | undefined = undefined;

      console.log("updating profile currentNetworkMembership", { profile });

      if (networkFromUrl) {
        // if url already contained the network, persist it
        const query = firestore
          .collectionGroup("members")
          .where("profileRef", "==", profileRef)
          .where("network.slug", "==", networkFromUrl);
        const networkMemberships = await query.get();
        if (!networkMemberships.empty) {
          currentNetworkMembership = networkMemberships.docs[0].data();
        }
      } else {
        // query networks and set the first one as current
        const query = firestore
          .collectionGroup("members")
          .where("profileRef", "==", profileRef);
        const networkMemberships = await query.get();
        if (!networkMemberships.empty) {
          currentNetworkMembership = networkMemberships.docs[0].data();
        }
      }

      if (currentNetworkMembership) {
        await profileRef.set(
          {
            currentNetworkMembership,
          },
          { merge: true }
        );
        return currentNetworkMembership;
      } else {
        console.warn("No networks available for this user!");
        return undefined;
      }
      // TODO handle the case where the user doesn't belong to any
      // networks. Show them a friendly error page asking them to talk to
      // their Admin and ask for an invite or something.
    }
  }, [firestore, profileRef, networkFromUrl, profile]);

  useEffect(() => {
    // any time the profile changes, attempt to reset current network
    // membership, if not already set
    setCurrentNetworkMembership();
  }, [profile, setCurrentNetworkMembership]);

  useEffect(() => {
    return auth.onIdTokenChanged(async (firebaseUser) => {
      if (firebaseUser) {
        console.log("onIdTokenChanged, updated cookie");
        updateCookie(firebaseUser);
      } else {
        // delete the cookie to ensure they aren't still signed on on the
        // server
        removeCookie();
      }
    });
  }, []);

  const getTimezoneString = () =>
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  const handleUserLogin = async (firebaseUser: firebase.User | null) => {
    if (firebaseUser) {
      // upon login determine whether the user has a network set
      const networkMembership = await setCurrentNetworkMembership();

      // fetch profile after setting network membership
      const profileRef = firestore.collection("profiles").doc(firebaseUser.uid);

      // update their cookie and set timezone on their profile every time they
      // login
      updateCookie(firebaseUser);
      const timezone = getTimezoneString();
      console.log("onAuthStateChanged, updated cookie", {
        timezone,
        firebaseUser,
      });

      await profileRef.set({ timezone }, { merge: true });
      // redirect to newsfeed (only if the user is on the home page)
      if (networkMembership && router.pathname === "/") {
        router.push(newsFeedRoute(networkMembership.network.slug));
      }
    } else {
      console.log("user logged out", { pathname: router.pathname });
      clearFirestoreCache();
      postTokenToRN();
      // we need to navigate any tabs away from logged in pages so it doesn't
      // throw errors. in the future this could be more sophisticated and
      // detect whether the current route is a logged in or out route, and
      // redirect only if logged in route.

      // TODO re-enable this to fix logout error bugs
      // if (!["/", ...billingsMarketingUrls].includes(router.pathname)) {
      //   router.push("/");
      // }
    }
  };

  useEffect(() => {
    // regular login via any mechanism (email, social, etc)
    auth.onAuthStateChanged(async (firebaseUser) => {
      // when the user first signs in the profileRef won't be defined. maybe
      // create one here.
      console.log("onAuthStateChanged", { firebaseUser });
      handleUserLogin(firebaseUser);
    });

    // social sign in produces a redirect. in that case, make sure they have a
    // profile - if not, create it, and send them to onboarding flow
    auth.getRedirectResult().then(async (result) => {
      console.log("getRedirectResult", { result });
      if (result.credential) {
        // This gives you a Google Access Token.
        // var token = result.credential.accessToken;
      }
      const { user } = result;
      if (user) {
        // make sure they have a profile
        const { uid } = user;
        const profileRef = firestore.collection("profiles").doc(uid);

        const profileSnap = await profileRef.get();
        const profile = profileSnap.data();
        // NOTE: it might exist, because of the timezone updater, so also check
        // whether name is set
        if (
          !profileSnap.exists ||
          !profile ||
          (!profile.firstName && !profile.lastName)
        ) {
          console.log(
            "getRedirectResult: creating profile for new user via social sign in",
            { uid, profile }
          );
          const name = user.displayName || "";
          const [firstName, lastName] = name.split(" ", 2);
          const photoURL = user.photoURL;
          // create profile for new user that just signed in via social
          await profileRef.set({
            name,
            firstName,
            lastName,
            photoURL,
          });
          router.push("/onboarding/account");
        } else {
          console.log("getRedirectResult: profile already exists for user", {
            uid,
            profileSnap,
          });
        }
      }
    });
  }, [router, firestore, networkFromUrl, setCurrentNetworkMembership]);

  // force refresh the token every 10 minutes
  useEffect(() => {
    const handle = setInterval(async () => {
      const user = auth.currentUser;
      if (user) await user.getIdToken(true);
    }, 10 * 60 * 1000);
    // clean up setInterval
    return () => clearInterval(handle);
  }, []);

  return <></>;
};

export default Auth;
